<template>
  <div id="modal-base">
    <div class="modal-header">
      <div class="d-flex items-center" style="gap: 5px">
        <feather-icon
          @click="close()"
          class="cursor-pointer"
          size="22"
          icon="ArrowLeftCircleIcon"
        />

        <div class="modal-title">Seleção de {{ $service.getProductTerm() }}</div>
      </div>

      <i @click="close()" class="icon-close-circle-outline" />
    </div>

    <div class="search-box">
      <span class="search">
        <input-field @keyup="queueSearch()" placeholder="Buscar" v-model="search" />

        <i class="icon-search" />
      </span>

      <span v-if="hasRequest" class="d-flex justify-content-center my-1">
        <b-spinner />
      </span>
    </div>

    <infinite-scroll
      v-if="!hasRequest"
      @fetch="checkInfiniteScroll()"
      :hasRequest="hasScrollRequest"
      class="modal-content-holder nice-Vscroll"
    >
      <template v-slot:content>
        <div
          v-for="(product, index) in products"
          @click="simulateCheckClick(product)"
          :key="index"
          class="product-check"
        >
          <div
            v-if="!singularReturn"
            :class="{ 'fake-check-box-selected': product.selected }"
            class="fake-check-box"
          >
            <feather-icon icon="CheckIcon" size="12" color="#ffffff" />
          </div>

          <div class="product-info">
            <div class="product-name">{{ product.name }}</div>

            <div v-if="!allowPriceEdit && !pedagogicalWorkflow" class="product-description">
              #{{ product.id }} - {{ $service.formatBrlAmountSymbol(product.price) }}
            </div>

            <div v-else-if="!pedagogicalWorkflow" @click.stop class="product-description-holder">
              <div class="description-text">#{{ product.id }} -</div>

              <div v-if="editingPriceIndex === index" class="money-wrapper">
                <money
                  @blur.native="handlePriceEditBlur(product)"
                  @input="validatePriceInput(product)"
                  id="price-input"
                  class="price-input"
                  trim
                  :precision="2"
                  v-model="product.price"
                />

                <Icon class="price-icon" icon="lets-icons:check-fill" />
              </div>

              <div v-else @click.stop="handlePriceEdit(index)" class="description-text price-box">
                {{ $service.formatBrlAmountSymbol(product.price) }}

                <Icon class="edit-icon" icon="fe:edit" />
              </div>
            </div>
          </div>

          <input
            v-if="!singularReturn && useQuantity && !pedagogicalWorkflow"
            @input="validateQuantity(product)"
            @click.stop
            :class="{ hidden: !product.selected }"
            type="number"
            min="0"
            class="quantity-input"
            v-model.number="product.quantity"
          />
        </div>
      </template>
    </infinite-scroll>

    <div class="modal-buttons">
      <b-button @click="close()" variant="light">Cancelar</b-button>

      <b-button @click="emitSuccess()" variant="primary"> Confirmar </b-button>
    </div>
  </div>
</template>

<script>
import { BButton, BFormCheckbox, BSpinner } from "bootstrap-vue";
import { Icon } from "@iconify/vue2";
import { Money } from "v-money";

import InfiniteScroll from "@/views/components/base/InfiniteScroll.vue";
import InputField from "@/views/components/InputField.vue";

export default {
  components: {
    BButton,
    BFormCheckbox,
    BSpinner,
    Icon,
    Money,

    InfiniteScroll,
    InputField,
  },

  props: {
    selectedProductsIds: {
      type: Array,
      default: () => [],
    },

    selectedProducts: {
      type: Array,
      default: () => [],
    },

    singularReturn: {
      type: Boolean,
      default: false,
    },

    useQuantity: {
      type: Boolean,
      default: false,
    },

    allowPriceEdit: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      queueingSearch: null,

      hasRequest: false,
      hasScrollRequest: false,

      search: "",
      limit: 100,
      offset: 0,
      totalCount: 0,

      products: [],
      localSelectedProductsId: [],
      localSelectedProducts: [],

      editingPriceIndex: null,
    };
  },

  computed: {
    pedagogicalWorkflow() {
      return this.$service.isPedagogical();
    },
  },

  created() {
    this.setLocalSelectedProducts();
    this.fetchProducts();
  },

  methods: {
    close() {
      this.$emit("closed");
    },

    emitSuccess() {
      this.removeZeroQuantity();

      const selected = {
        ids: this.localSelectedProductsId,
        products: this.localSelectedProducts,
      };

      this.$emit("selected", selected);
      this.close();
    },

    removeZeroQuantity() {
      this.localSelectedProducts = this.localSelectedProducts.filter(
        (product) => product.quantity > 0
      );

      this.localSelectedProductsId = this.localSelectedProducts.map((product) => product.id);
    },

    simulateCheckClick(product) {
      if (this.singularReturn) {
        this.$emit("selected", product);
        this.close();

        return;
      }

      this.$set(product, "selected", !product.selected);

      this.addToSelectedList(product);
    },

    queueSearch() {
      window.clearTimeout(this.queueingSearch);

      this.queueingSearch = setTimeout(() => {
        this.fetchProducts();
      }, 850);
    },

    setLocalSelectedProducts() {
      this.localSelectedProductsId = this.selectedProductsIds;
      this.localSelectedProducts = this.selectedProducts;
    },

    validateQuantity(product) {
      let value = parseInt(product.quantity);

      if (isNaN(value) || value < 0) value = 0;

      this.$set(product, "quantity", value);

      const index = this.localSelectedProducts?.findIndex((p) => p.id === product.id);

      if (index !== -1) this.localSelectedProducts[index] = product;
    },

    addToSelectedList(product) {
      const index = this.localSelectedProductsId?.findIndex((p) => p === product.id);

      if (index === -1) {
        this.localSelectedProductsId.push(product.id);
        this.localSelectedProducts.push(product);

        this.$set(product, "quantity", 1);
      } else {
        this.localSelectedProductsId.splice(index, 1);
        this.localSelectedProducts.splice(index, 1);

        this.$delete(product, "quantity");
      }
    },

    setSelectProducts() {
      if (!this.localSelectedProducts) return;

      this.products.forEach((element) => {
        const product = this.localSelectedProducts.find((p) => p.id === element.id);

        this.$set(element, "selected", !!product);

        if (product) {
          this.$set(element, "quantity", product.quantity);
          this.$set(element, "price", product.price);
        }
      });
    },

    async handlePriceEdit(index) {
      if (this.editingPriceIndex) return;

      this.editingPriceIndex = index;

      await this.$nextTick();

      document.getElementById("price-input").focus();
    },

    handlePriceEditBlur(product) {
      const productIndex = this.localSelectedProducts.findIndex((p) => p.id === product.id);

      if (productIndex !== -1) {
        this.localSelectedProducts[productIndex] = product;
      }

      setTimeout(() => {
        this.editingPriceIndex = null;
      }, 100);
    },

    validatePriceInput(product) {
      if (product.price < 0) this.$set(product, "price", 0);
    },

    checkInfiniteScroll() {
      if (this.totalCount > this.offset) {
        this.offset += this.limit;

        this.fetchProducts(true);
      }
    },

    fetchProducts(fromScroll = false) {
      if (fromScroll) {
        this.hasScrollRequest = true;
      } else {
        this.hasRequest = true;

        this.offset = 0;
      }

      const params = [
        {
          query: "limit",
          param: this.limit,
        },

        {
          query: "offset",
          param: this.offset,
        },

        {
          query: "search",
          param: this.search,
        },

        {
          query: "workflow_id",
          param: this.$service.getWorkflowId(),
        },
      ];

      this.$store
        .dispatch("opportunityStore/fetchProducts", this.$service.formatQueryParam(params))
        .then((response) => {
          this.totalCount = response.pagination.total_count;

          if (fromScroll) {
            this.products = [...this.products, ...response.data];
          } else {
            this.products = response.data;
          }

          this.setSelectProducts();
        })
        .finally(() => {
          this.hasRequest = false;
          this.hasScrollRequest = false;
        });
    },
  },
};
</script>

<style lang="scss" scoped>
#modal-base {
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100dvh;
  background-color: #ffffff;
  overflow: hidden;

  .modal-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
    padding: 15px 10px;
    border-bottom: 1px solid #ececec;

    .modal-title {
      font-size: 15px;
      font-weight: 600;
    }

    .icon-close-circle-outline {
      font-size: 22px;
      cursor: pointer;
    }
  }

  .search-box {
    padding: 1rem;

    .search {
      padding: 0px;
      margin: 0px;
      position: relative;
      border: 1px solid #e5e5e5;
      display: flex;

      .input-field-container {
        padding: 0px;
        margin: 0px;
        :deep(input) {
          border: none;
        }
      }

      .icon-search {
        position: absolute;
        right: 10px;
        line-height: 45px;
      }
    }
  }

  .modal-content-holder {
    display: flex;
    flex-direction: column;
    align-self: center;
    height: 100%;
    width: calc(100% - 2rem);
    margin-bottom: 10px;
    gap: 5px;
    border: 1px solid #ececec;
    overflow-x: hidden;

    .product-check {
      display: flex;
      align-items: center;
      word-break: break-word;
      width: 100%;
      padding: 10px;
      gap: 10px;
      border-bottom: 1px solid #ececec;
      cursor: pointer;

      .fake-check-box {
        display: flex;
        justify-content: center;
        align-items: center;
        min-height: 20px;
        min-width: 20px;
        border-radius: 5px;
        border: 1px solid #e5e5e5;
      }

      .fake-check-box-selected {
        background-color: #046af3;
        color: #ffffff;
      }

      .product-info {
        display: flex;
        flex-direction: column;
        text-align: left;
        width: 100%;

        .product-name {
          font-size: 14px;
          color: #525c68;
        }

        .product-description {
          font-size: 14px;
          color: #9c9c9c;
        }

        .product-description-holder {
          display: flex;
          align-items: center;
          height: 22px;
          width: 100%;
          gap: 5px;

          .description-text {
            font-size: 14px;
            color: #9c9c9c;
          }

          .price-box {
            position: relative;
            flex-grow: 1;
            cursor: pointer;

            .edit-icon {
              display: none;
              position: absolute;
              font-size: 14px;
              right: 0;
              top: 50%;
              transform: translateY(-50%);
              background-color: #ffffff;
              color: #046af3;
            }

            &:hover {
              .edit-icon {
                display: block;
              }
            }
          }

          .money-wrapper {
            flex-grow: 1;
            position: relative;

            .price-input {
              width: 100%;
              border-radius: 5px;
              border: 1px solid #e5e5e5;
              outline: none;
            }

            .price-icon {
              position: absolute;
              font-size: 18px;
              right: 5px;
              top: 50%;
              transform: translateY(-50%);
              background-color: #ffffff;
              color: #046af3;
            }
          }
        }
      }

      .quantity-input {
        width: 45px;
        height: 30px;
        padding: 5px 10px;
        border-radius: 5px;
        border: 1px solid #e5e5e5;
        outline: none;
      }
    }

    .product-check:last-child {
      border-bottom: none;
    }

    .product-check:only-child {
      border-bottom: 1px solid #ececec;
    }
  }

  .modal-buttons {
    display: flex;
    justify-content: space-around;
    align-items: center;
    font-weight: bold;
    width: 100%;
    padding: 10px;
    margin-top: auto;
    border-top: 1px solid #ececec;
    color: #000000;
    background: #ffffff;
  }

  .hidden {
    display: none;
  }
}
</style>
